import type { ApiClient } from '../api'
import {
	apiRouteBuilder,
	deleteTrainingBundleCurriculum,
	disableCertification,
	getCertifications,
	getCurriculumPackages,
	getSharedCompletionCourses,
	getTrainingCurriculumsList,
	postCreateCertification,
	postCreateCurriculum,
	postReleaseCertification,
	postReleaseCertificationToAgency,
	postReleaseCurriculum,
} from '../apiRoutes'
import type {
	Agency,
	Certification,
	Curriculum,
	PayerSource,
	SharedCompletion,
} from './interfaces'

export async function createCertification(api: ApiClient, name: string) {
	const { data } = await api.post<ApiCertificationResponse>(postCreateCertification, { curriculum: { name } })

	return data
}

export async function createCurriculum(
	api: ApiClient, { stateRequirement }: { stateRequirement: CurriculumStateRequirements }
) {
	await api.post(postCreateCurriculum, { state_requirement: stateRequirement })
}

export async function deleteCertification(
	api: ApiClient, { id }: { id: number }) {
	const { data } = await api.delete<ApiDeleteCurriculumResponse>(disableCertification, { id })

	return data
}

export async function deleteCurriculum(api: ApiClient, id: number) {
	const { data } = await api.delete<ApiDeleteCurriculumResponse>(deleteTrainingBundleCurriculum, { id })

	return data
}

export async function getCertificationsList(
	api: ApiClient) {
	const { data } = await api.get<ApiCertificationsResponse>(getCertifications)

	return data
}

export async function getCurriculums(api: ApiClient) {
	const { data } = await api.get<ApiTrainingCurriculumsResponse>(getTrainingCurriculumsList)

	return data
}

export async function getSharedCompletions(api: ApiClient, memberId: number) {
	const route = apiRouteBuilder(getSharedCompletionCourses, { memberId })
	const { data } = await api.get<ApiSharedCompletionsResponse>(route)

	return data
}

export async function getShowCurriculumPackages(
	api: ApiClient,
	payload: ApiShowCurriculumPackagesPayload
) {
	const { data } = await api.get<ApiShowCurriculumPackagesResponse>(getCurriculumPackages, payload)

	return data.packages
}

export async function releaseCertification(api: ApiClient, {
	curriculumIds,
	legacyTier,
	tier2,
}: {
	curriculumIds: number[],
	legacyTier?: boolean,
	tier2?: boolean,
}) {
	const payload = {
		curriculum_ids: curriculumIds,
		legacyTier,
		tier2,
	}

	const { data } = await api.post<ApiReleaseCurriculumResponse>(postReleaseCertification, payload)

	return data
}

export async function releaseCertificationToAgency(
	api: ApiClient, { agencyId, curriculumId }: {
		agencyId: number, curriculumId: number,
	}) {
	const payload = { agency_id: agencyId, curriculum_id: curriculumId }

	const { data } = await api.post<ApiReleaseCurriculumResponse>(postReleaseCertificationToAgency, payload)

	return data
}

export async function releaseCurriculum(api: ApiClient, {
	agency,
	curriculums,
	merge,
	mergedTrackName,
}: {
	agency: Agency | undefined,
	curriculums: Curriculum[],
	merge: boolean,
	mergedTrackName: string,
}) {
	const payload = {
		agency,
		curriculums,
		merge,
		merged_track_name: mergedTrackName,
	}

	const { data } = await api.post<ApiReleaseCurriculumResponse>(postReleaseCurriculum, payload)

	return data
}

export interface CurriculumStateRequirements {
	name: string
	payer_source: PayerSource
	state: string
}

export interface ApiShowCurriculumPackagesPayload {
	id: number
}

export interface ApiShowCurriculumPackagesResponse { packages: string[] }

type ApiCertificationResponse = Certification

export interface ApiCertificationsResponse {
	curriculums: Certification[]
	newCurriculumPath: string
	releaseCurriculumPath: string
}

export interface ApiDeleteCurriculumResponse {
	message: string
}

export interface ApiSharedCompletionsResponse {
	shared_completions: SharedCompletion[]
}

export interface ApiReleaseCurriculumResponse {
	agency_name: string
	agency_path: string
	message: string
	number_of_tracks: number
	track_id: number
	track_name: string
}

export interface ApiTrainingCurriculumsResponse {
	agencies: { id: number, name: string }[]
	curriculums: Curriculum[]
	states: string[][]
}

export interface ApiSharedCompletionsResponse {
	shared_completions: SharedCompletion[]
}
